import React, { useState, useEffect, useCallback } from "react";
import {
  Container, Row, Col, Card, Table, Badge, Button, Modal,
  Alert, Spinner, Form,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { getIntakeReviews, getIntakeReview, resolveIntakeReview } from "../intakeApi";

const REASON_LABELS = {
  DUPLICATE_PATIENT_ID: "Duplicate patient ID",
  DUPLICATE_PATIENT_DOB_EMAIL: "Duplicate DOB + email",
  DUPLICATE_PATIENT_DOB_NAME: "Duplicate DOB + name",
  DUPLICATE_ACCOUNT_ID: "Duplicate account ID",
  DUPLICATE_MEDICAL_AID_NUMBER: "Duplicate medical aid number",
  NO_PATIENT_MATCH: "No patient found",
  MULTIPLE_PATIENT_MATCHES: "Multiple patient matches",
  NO_ACCOUNT_MATCH: "No account found",
  MULTIPLE_ACCOUNT_MATCHES: "Multiple account matches",
  FAMILYCHECK_FAILED: "FamilyCheck failed",
  FAMILYCHECK_NO_DEPENDANTS: "FamilyCheck — no dependants",
  PATIENT_ALREADY_LINKED: "Patient already linked",
};

const FLOW_LABELS = {
  A: "New + Medical Aid",
  B: "New + Private",
  C: "Existing + Medical Aid",
  D: "Existing + Private",
};

export function IntakeReviews() {
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [page, setPage] = useState(1);

  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewDetail, setReviewDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [resolving, setResolving] = useState(false);
  const [resolveAction, setResolveAction] = useState("");
  const [resolvePatientId, setResolvePatientId] = useState("");
  const [resolveAccountId, setResolveAccountId] = useState("");
  const [resolveError, setResolveError] = useState("");
  const [resolveSuccess, setResolveSuccess] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getIntakeReviews(statusFilter, page);
      setReviews(data.reviews || []);
      setTotal(data.total || 0);
    } catch {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  async function openReview(review) {
    setSelectedReview(review);
    setReviewDetail(null);
    setResolveAction("");
    setResolvePatientId("");
    setResolveAccountId("");
    setResolveError("");
    setResolveSuccess(false);
    setDetailLoading(true);
    try {
      const detail = await getIntakeReview(review._id);
      setReviewDetail(detail);
    } catch {
      setReviewDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleResolve() {
    if (!resolveAction) { setResolveError("Please choose an action."); return; }
    setResolveError("");
    setResolving(true);
    try {
      await resolveIntakeReview(
        selectedReview._id,
        resolveAction,
        resolvePatientId || null,
        resolveAccountId || null
      );
      setResolveSuccess(true);
      load();
    } catch (err) {
      setResolveError(err?.response?.data?.error || "Failed to resolve. Please try again.");
    } finally {
      setResolving(false);
    }
  }

  function closeModal() {
    setSelectedReview(null);
    setReviewDetail(null);
    setResolveSuccess(false);
  }

  function formatDate(d) {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">Intake Reviews</h4>
          <div className="text-muted small">Submissions that require staff review</div>
        </Col>
        <Col xs="auto">
          <Form.Select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ width: 160 }}
          >
            <option value="PENDING">Pending</option>
            <option value="RESOLVED">Resolved</option>
            <option value="DISMISSED">Dismissed</option>
          </Form.Select>
        </Col>
      </Row>

      <Card className="shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center text-muted py-5">
              No {statusFilter.toLowerCase()} reviews.
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Date</th>
                  <th>Practice</th>
                  <th>Flow</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((r) => (
                  <tr key={r._id}>
                    <td className="small">{formatDate(r.createdAt)}</td>
                    <td className="small">{r.practiceNumber}</td>
                    <td>
                      <Badge bg="secondary">{FLOW_LABELS[r.flowType] || r.flowType}</Badge>
                    </td>
                    <td className="small">{REASON_LABELS[r.reason] || r.reason}</td>
                    <td>
                      <Badge bg={r.status === "PENDING" ? "warning" : r.status === "RESOLVED" ? "success" : "secondary"}>
                        {r.status}
                      </Badge>
                    </td>
                    <td>
                      <Button size="sm" variant="outline-primary" onClick={() => openReview(r)}>
                        {r.status === "PENDING" ? "Review" : "View"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
        {total > 50 && (
          <Card.Footer className="d-flex justify-content-between align-items-center small text-muted">
            <span>Showing {reviews.length} of {total}</span>
            <div className="d-flex gap-2">
              <Button size="sm" variant="outline-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <Button size="sm" variant="outline-secondary" disabled={reviews.length < 50} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </Card.Footer>
        )}
      </Card>

      {/* Review detail modal */}
      <Modal show={!!selectedReview} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Intake Review — {selectedReview && (FLOW_LABELS[selectedReview.flowType] || selectedReview.flowType)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailLoading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : resolveSuccess ? (
            <Alert variant="success">Review resolved successfully.</Alert>
          ) : reviewDetail ? (
            <>
              <Alert variant="warning" className="small py-2">
                <strong>Reason:</strong> {REASON_LABELS[reviewDetail.reason] || reviewDetail.reason}
              </Alert>

              {/* Submitted data */}
              <h6>Submitted Data</h6>
              <Table size="sm" bordered className="mb-4">
                <tbody>
                  {reviewDetail.submittedData?.patient && Object.entries(reviewDetail.submittedData.patient)
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                      <tr key={k}>
                        <td className="text-muted small" style={{ width: "40%" }}>{k}</td>
                        <td className="small">{v}</td>
                      </tr>
                    ))}
                </tbody>
              </Table>

              {/* Candidate patients */}
              {reviewDetail.candidatePatients?.length > 0 && (
                <>
                  <h6>Candidate Patients</h6>
                  <Table size="sm" bordered className="mb-4">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th><th>DOB</th><th>ID Number</th><th>Email</th><th>ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviewDetail.candidatePatients.map((p) => (
                        <tr key={p._id}>
                          <td className="small">{p.patientFirstName} {p.patientLastName}</td>
                          <td className="small">{p.patientDOB}</td>
                          <td className="small">{p.patientIdNumber}</td>
                          <td className="small">{p.patientEmail}</td>
                          <td className="small text-muted" style={{ fontSize: "0.7rem" }}>{p._id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {/* Candidate accounts */}
              {reviewDetail.candidateAccounts?.length > 0 && (
                <>
                  <h6>Candidate Accounts</h6>
                  <Table size="sm" bordered className="mb-4">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th><th>DOB</th><th>ID Number</th><th>Email</th><th>ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviewDetail.candidateAccounts.map((a) => (
                        <tr key={a._id}>
                          <td className="small">{a.accountFirstName} {a.accountLastName}</td>
                          <td className="small">{a.accountDOB}</td>
                          <td className="small">{a.accountIdNumber}</td>
                          <td className="small">{a.accountEmail}</td>
                          <td className="small text-muted" style={{ fontSize: "0.7rem" }}>{a._id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}

              {/* Resolution actions (only for PENDING) */}
              {selectedReview?.status === "PENDING" && (
                <>
                  <hr />
                  <h6>Resolve</h6>
                  {resolveError && <Alert variant="danger" className="small py-2">{resolveError}</Alert>}

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold">Action</Form.Label>
                    <div className="d-flex gap-2 flex-wrap">
                      {[
                        ["created", "Mark as Created", "success"],
                        ["linked", "Link to Existing", "primary"],
                        ["dismissed", "Dismiss", "secondary"],
                      ].map(([val, lbl, variant]) => (
                        <Button
                          key={val}
                          size="sm"
                          variant={resolveAction === val ? variant : `outline-${variant}`}
                          onClick={() => setResolveAction(val)}
                        >
                          {lbl}
                        </Button>
                      ))}
                    </div>
                  </Form.Group>

                  {resolveAction === "linked" && (
                    <Row>
                      <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="small">Patient ID (MongoDB _id)</Form.Label>
                          <Form.Control
                            size="sm"
                            placeholder="Paste patient _id"
                            value={resolvePatientId}
                            onChange={(e) => setResolvePatientId(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="small">Account ID (MongoDB _id)</Form.Label>
                          <Form.Control
                            size="sm"
                            placeholder="Paste account _id"
                            value={resolveAccountId}
                            onChange={(e) => setResolveAccountId(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}
                </>
              )}

              {selectedReview?.status !== "PENDING" && selectedReview?.resolution && (
                <Alert variant="secondary" className="small">
                  <strong>Resolved by:</strong> {selectedReview.resolution.resolvedBy} on{" "}
                  {formatDate(selectedReview.resolution.resolvedAt)} — Action: {selectedReview.resolution.action}
                </Alert>
              )}
            </>
          ) : (
            <Alert variant="danger">Failed to load review details.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>Close</Button>
          {selectedReview?.status === "PENDING" && !resolveSuccess && !detailLoading && (
            <Button variant="primary" onClick={handleResolve} disabled={resolving || !resolveAction}>
              {resolving ? <><Spinner size="sm" animation="border" className="me-2" />Saving...</> : "Save Resolution"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
